/**
 * Line-level diff utility using a Myers-style LCS algorithm.
 *
 * Pure functions — no side effects, no DOM, no engine dependencies.
 * Used by useBlame (local overlay) and potentially by engine blame/diffText.
 */

// --- Public types ---

export interface LineMapping {
    /** Index in oldLines (null = inserted line with no old counterpart) */
    oldIndex: number | null
    /** Index in newLines */
    newIndex: number
}

// --- Myers diff core ---

/**
 * Compute a shortest edit script between two string arrays using
 * the classic Myers O(ND) algorithm, then convert the result to a
 * line mapping that tells the caller where each new line came from.
 *
 * Performance: O(N·D) where N = max(a.length, b.length) and D = edit
 * distance.  For typical "insert a few lines" edits D is tiny, so this
 * runs in microseconds even on multi-thousand-line files.
 */
export function computeLineMapping(
    oldLines: readonly string[],
    newLines: readonly string[],
): LineMapping[] {
    const n = oldLines.length
    const m = newLines.length

    // Fast path: identical content
    if (n === m) {
        let same = true
        for (let i = 0; i < n; i++) {
            if (oldLines[i] !== newLines[i]) { same = false; break }
        }
        if (same) {
            return newLines.map((_, i) => ({ oldIndex: i, newIndex: i }))
        }
    }

    // Fast path: old was empty (all insertions)
    if (n === 0) {
        return newLines.map((_, i) => ({ oldIndex: null, newIndex: i }))
    }

    // Fast path: new is empty (all deletions → empty mapping)
    if (m === 0) {
        return []
    }

    // --- Myers shortest-edit-script ---
    // We compute the SES and then walk it to build the mapping.

    const max = n + m
    // v stores the best x-position reached for each diagonal k.
    // Indexed as v[k + offset] where offset = max.
    const size = 2 * max + 1
    const v = new Int32Array(size)
    v.fill(-1)
    v[1 + max] = 0  // k=1 with offset

    // Store trace for backtracking
    const trace: Int32Array[] = []

    let found = false
    for (let d = 0; d <= max; d++) {
        // Snapshot v for backtracking
        trace.push(v.slice())

        for (let k = -d; k <= d; k += 2) {
            const kIdx = k + max

            // Decide whether to go down or right
            let x: number
            if (k === -d || (k !== d && v[kIdx - 1] < v[kIdx + 1])) {
                x = v[kIdx + 1]  // move down (insert)
            } else {
                x = v[kIdx - 1] + 1  // move right (delete)
            }
            let y = x - k

            // Follow diagonal (matching lines)
            while (x < n && y < m && oldLines[x] === newLines[y]) {
                x++
                y++
            }

            v[kIdx] = x

            if (x >= n && y >= m) {
                found = true
                break
            }
        }
        if (found) break
    }

    // Backtrack through trace to reconstruct edit operations
    // Each operation is: 'match' (diagonal), 'insert' (y++), 'delete' (x++)
    const ops: Array<'match' | 'insert' | 'delete'> = []

    let x = n
    let y = m
    for (let d = trace.length - 1; d >= 0; d--) {
        const vPrev = trace[d]
        const k = x - y
        const kIdx = k + max

        let prevK: number
        if (k === -d || (k !== d && vPrev[kIdx - 1] < vPrev[kIdx + 1])) {
            prevK = k + 1  // came from insert (k+1 → k)
        } else {
            prevK = k - 1  // came from delete (k-1 → k)
        }

        const prevX = vPrev[prevK + max]
        const prevY = prevX - prevK

        // Diagonal moves (matches) after the edit step
        while (x > prevX && y > prevY) {
            x--
            y--
            ops.push('match')
        }

        // The edit step itself
        if (d > 0) {
            if (x === prevX) {
                // y decreased → insert
                y--
                ops.push('insert')
            } else {
                // x decreased → delete
                x--
                ops.push('delete')
            }
        }
    }

    ops.reverse()

    // Convert operations to LineMapping[]
    const mapping: LineMapping[] = []
    let oi = 0  // old index
    let ni = 0  // new index

    for (const op of ops) {
        switch (op) {
            case 'match':
                mapping.push({ oldIndex: oi, newIndex: ni })
                oi++
                ni++
                break
            case 'insert':
                mapping.push({ oldIndex: null, newIndex: ni })
                ni++
                break
            case 'delete':
                // Old line removed — no entry in mapping (it maps old→new)
                oi++
                break
        }
    }

    return mapping
}
